import React, { FC, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import Checkbox from 'components/checkbox';
import { CountrySelectInput } from 'components/country-select-input';
import { FloatingLabelInput } from 'components/floating-label-fields';
import LoadingAnimation from 'components/loading-animation';
import useCreditCardFormProps from 'hooks/form/use-credit-card-form-props';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { PaymentFieldVariations, defaultFlagValues } from 'state/launchdarkly/variations';
import { usePaymentContext } from 'state/payment';
import { CardType } from 'state/payment/types';
import { FormValidationState } from 'utils/form';
import { IAdyenPaymentState, IPaymentErrors, IPaymentState, excludeNumeric } from 'utils/payment';

import AdyenCustomCardInputs from './adyen-credit-card-form-inputs/adyen-credit-card-inputs';
import { IAdyenErrors, IAdyenState } from './adyen-credit-card-form-inputs/types';
import { BillingSameAsDeliveryLabel, SaveCardForFutureLabel } from './credit-card-form-labels';
import FormTitle from './credit-card-form-title';
import FirstDataCreditCardInputs from './first-data-credit-card-form-inputs/first-data-credit-card-inputs';
import { messages } from './messages';

interface IFormContainer {
  withBorder: boolean;
  isHidden?: boolean;
}

export const FormContainer = styled.div<IFormContainer>`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  background-color: transparent;
  font-size: 0.75rem;
  visibility: ${p => (p.isHidden ? 'hidden' : 'visible')};

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }

  ${Styles.mobile`
    margin-top: 1rem;
  `}

  ${Styles.desktop`
    padding: 1rem;

    ${(p: IFormContainer) =>
      p.withBorder &&
      css`
        border: 1px solid ${Styles.color.grey.four};
        background-color: white;
      `}
    `}
`;

const StyledFormContainer = styled(FormContainer)`
  ${Styles.mobile`
    margin-top: 0;
  `}

  ${Styles.desktop`
     padding: 0;
  `}
`;

export const NameOnCardWrapper = styled.div`
  flex-basis: 100%;
`;

export const CountrySelectWrapper = styled.div`
  flex-basis: 100%;
`;

const StreetAddressWrapper = styled.div`
  flex-basis: 100%;
`;

const BillingAptWrapper = styled.div`
  flex-basis: 45%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 19%;
    margin-right: 5%;
  `}
`;
const BillingZipWrapper = styled.div`
  flex-basis: 45%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const BillingCityWrapper = styled.div`
  flex-basis: 60%;
  margin-right: 5%;
  ${Styles.desktop`
    flex-basis: 30%;
    margin-right: 5%;
  `}
`;

const BillingStateWrapper = styled.div`
  flex-basis: 35%;
  ${Styles.desktop`
    flex-basis: 16%;
    margin-right: 5%;
  `}
`;

interface ICreditCardFormInputs {
  errors: IPaymentErrors;
  isDelivery?: boolean;
  onChange: (name: string, value: string) => void;
  onAdyenChange?: (state: IAdyenState) => void;
  onAdyenError?: (errors: IAdyenErrors) => void;
  paymentValues: IPaymentState | IAdyenPaymentState;
  showSaveCard?: boolean;
  withBorder?: boolean;
  ariaLabel?: string;
  supportedCardTypes?: CardType[];
}

const CreditCardFormInputs: FC<ICreditCardFormInputs> = ({
  errors,
  isDelivery = false,
  onChange,
  onAdyenChange,
  onAdyenError,
  paymentValues,
  showSaveCard = true,
  withBorder = false,
  ariaLabel,
  supportedCardTypes = []
}) => {
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) || defaultFlagValues;
  const { feCountryCode } = useLocale();
  // If the country field is hidden from the form, use the user's current locale
  const country = paymentFieldVariations.country ? paymentValues.billingCountry : feCountryCode;

  // retrieve additional credit card form props
  const { postalCodeInputProps, regionInputProps } = useCreditCardFormProps(country ?? undefined);
  const { formatMessage } = useIntl();
  const { isAdyen } = usePaymentContext();
  const [isAdyenInputsLoading, setIsAdyenInputsLoading] = useState(true);
  const onAdyenInputsReady = useCallback(() => {
    setIsAdyenInputsLoading(false);
  }, []);

  const isFormLoading = isAdyen ? isAdyenInputsLoading : false;

  const handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement> = e =>
    onChange(e.target.name, e.target.value);

  return (
    <>
      {isFormLoading && <LoadingAnimation fillColor={Styles.color.primary} />}
      <FormContainer withBorder={withBorder} aria-label={ariaLabel} isHidden={isFormLoading}>
        <FormTitle
          title={formatMessage(messages.pleaseEnterYourCreditCardInformation)}
          creditText={formatMessage(messages.credit)}
        />
        {paymentFieldVariations.name && (
          <NameOnCardWrapper>
            <FloatingLabelInput
              label={formatMessage(messages.nameOnCard)}
              name="nameOnCard"
              data-testid="nameOnCard"
              value={paymentValues.nameOnCard}
              onChange={handleChange}
              required
              autocomplete="cc-name"
              validation={errors.nameOnCard ? FormValidationState.INVALID : undefined}
              message={errors.nameOnCard}
            />
          </NameOnCardWrapper>
        )}
        {isAdyen ? (
          <AdyenCustomCardInputs
            onChange={onAdyenChange}
            onError={onAdyenError}
            onAdyenInputsReady={onAdyenInputsReady}
          />
        ) : (
          <FirstDataCreditCardInputs
            onChange={handleChange}
            supportedCardTypes={supportedCardTypes}
            paymentValues={paymentValues}
            errors={errors}
            paymentFieldVariations={paymentFieldVariations}
          />
        )}
        {isDelivery && (
          <Checkbox
            data-testid="billing-same-as-delivery"
            label={<BillingSameAsDeliveryLabel />}
            name="billingAddressSameAsDelivery"
            checked={paymentValues.billingAddressSameAsDelivery}
            onChange={handleChange}
          />
        )}
        <StyledFormContainer withBorder={false} isHidden={isFormLoading}>
          {paymentFieldVariations.addressLine1 && (
            <StreetAddressWrapper>
              <FloatingLabelInput
                label={formatMessage(messages.streetAddress)}
                name="billingStreetAddress"
                data-testid="billingStreetAddress"
                value={paymentValues.billingStreetAddress}
                onChange={handleChange}
                required
                autocomplete="billing street-address"
                validation={errors.billingStreetAddress ? FormValidationState.INVALID : undefined}
                message={errors.billingStreetAddress}
                readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
              />
            </StreetAddressWrapper>
          )}
          {paymentFieldVariations.addressLine2 && (
            <BillingAptWrapper>
              <FloatingLabelInput
                label={formatMessage(messages.aptOrSuite)}
                aria-label={formatMessage(messages.apartment)}
                name="billingApt"
                data-testid="billingApt"
                value={paymentValues.billingApt}
                onChange={handleChange}
                validation={errors.billingApt ? FormValidationState.INVALID : undefined}
                message={errors.billingApt}
                autocomplete="billing street-apt"
                readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
              />
            </BillingAptWrapper>
          )}
          {paymentFieldVariations.city && (
            <BillingCityWrapper>
              <FloatingLabelInput
                label={formatMessage(messages.city)}
                name="billingCity"
                data-testid="billingCity"
                value={paymentValues.billingCity}
                onChange={handleChange}
                required
                type="text"
                autocomplete="billing locality"
                validation={errors.billingCity ? FormValidationState.INVALID : undefined}
                message={errors.billingCity}
                readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
              />
            </BillingCityWrapper>
          )}
          {paymentFieldVariations.state && (
            <BillingStateWrapper>
              <FloatingLabelInput
                name="billingState"
                data-testid="billingState"
                value={excludeNumeric(paymentValues.billingState ?? '')}
                onChange={handleChange}
                required
                autocomplete="billing region"
                validation={errors.billingState ? FormValidationState.INVALID : undefined}
                message={errors.billingState}
                readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
                {...regionInputProps}
              />
            </BillingStateWrapper>
          )}
          {paymentFieldVariations.zip && (
            <BillingZipWrapper>
              {/* Additional props are spread: label, maxLength, pattern */}
              <FloatingLabelInput
                name="billingZip"
                data-testid="billingZip"
                value={paymentValues.billingZip}
                onChange={handleChange}
                required
                autocomplete="billing postal-code"
                validation={errors.billingZip ? FormValidationState.INVALID : undefined}
                message={errors.billingZip}
                readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
                maxLength={10}
                {...postalCodeInputProps}
              />
            </BillingZipWrapper>
          )}
          {paymentFieldVariations.country && (
            <CountrySelectWrapper>
              <CountrySelectInput
                label={formatMessage(messages.country)}
                name="billingCountry"
                data-testid="billingCountry"
                required
                value={paymentValues.billingCountry ?? ''}
                onChange={handleChange}
                autocomplete="billing country"
                validation={errors.billingCountry ? FormValidationState.INVALID : undefined}
                message={errors.billingCountry}
                readOnly={!!(isDelivery && paymentValues.billingCountry)}
              />
            </CountrySelectWrapper>
          )}
        </StyledFormContainer>
        {showSaveCard && (
          <Checkbox
            label={<SaveCardForFutureLabel />}
            name="saveCard"
            data-testid="saveCard"
            checked={paymentValues.saveCard}
            onChange={handleChange}
          />
        )}
      </FormContainer>
    </>
  );
};

export default CreditCardFormInputs;
